import { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CurrentUserContext } from '../../../assets/contexts/currentUserContext';
import { PostAuthRedirectContext } from '../../../assets/contexts/postAuthRedirectContext';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import {
  BIOMETRIC_ENROLLMENT_STATUS,
  useBiometricEnrollment,
} from '../../../assets/hooks/useBiometricEnrollment';
import { getCards, getPostUnlockPath, refreshCards } from '../../../assets/services/cards';
import { markUnlockSessionReady } from '../../../assets/services/unlockSession';
import PinPage from '../PinPage/PinPage';
import SuccessCheckBadge from '../../SuccessCheckBadge/SuccessCheckBadge';
import BottomActionButton from '../../shared/BottomActionButton/BottomActionButton';
import { BiometricIcon, ShieldIcon } from '../../../assets/icons/icons';
import './PasscodeSet.css';

const SCREEN_STATE = {
  PASSCODE_SET: 'passcode_set',
  BIOMETRIC_SUCCESS: 'biometric_success',
  BIOMETRIC_DENIED: 'biometric_denied',
};

function PasscodeSet() {
  const navigate = useNavigate();
  const { currentUser } = useContext(CurrentUserContext);
  const { consumePostAuthRedirect } = useContext(PostAuthRedirectContext);
  const { common, pin, profile, t } = useContext(TranslationContext);
  const biometricEnrollment = useBiometricEnrollment();
  const [screenState, setScreenState] = useState(SCREEN_STATE.PASSCODE_SET);
  const [isNavigating, setIsNavigating] = useState(false);
  const shouldPromoteBiometrics = screenState === SCREEN_STATE.PASSCODE_SET
    && biometricEnrollment.availability.isAvailable
    && !biometricEnrollment.availability.isEnabled;
  const isBiometricSuccess = screenState === SCREEN_STATE.BIOMETRIC_SUCCESS;
  const isBiometricDenied = screenState === SCREEN_STATE.BIOMETRIC_DENIED;
  const title = isBiometricSuccess
    ? profile.biometric.enabledTitle
    : isBiometricDenied
      ? profile.biometric.notEnabledTitle
      : pin.setDoneTitle;
  const subtitle = isBiometricSuccess
    ? t('profile.biometric.enabledSuccessText', {
      productName: common.appName,
      label: biometricEnrollment.biometricLabel,
    })
    : isBiometricDenied
      ? profile.biometric.notEnabledText
      : shouldPromoteBiometrics
        ? t('profile.biometric.enableText', {
          productName: common.appName,
          label: biometricEnrollment.biometricLabel,
        })
        : pin.setDoneText;
  const BadgeIcon = shouldPromoteBiometrics || isBiometricSuccess
    ? BiometricIcon
    : ShieldIcon;
  const navigateNext = useCallback(async () => {
    if (isNavigating) {
      return;
    }

    setIsNavigating(true);
    markUnlockSessionReady();

    let nextPath = getPostUnlockPath(currentUser);

    if (currentUser && nextPath !== '/home') {
      try {
        const cards = await refreshCards({ limit: 25 });

        if (cards.length > 0) {
          nextPath = '/home';
        }
      } catch (error) {
        if (getCards().length > 0) {
          nextPath = '/home';
        }
      }
    }

    const targetPath = consumePostAuthRedirect(nextPath);

    navigate(targetPath === '/cards/new' ? '/cards/new?hideBack=1' : targetPath, {
      replace: true,
      state: { fromPasscodeSet: true, hideBack: targetPath === '/cards/new' },
    });
  }, [consumePostAuthRedirect, currentUser, isNavigating, navigate]);

  const handleEnableBiometric = async () => {
    const result = await biometricEnrollment.startEnrollment();

    if (result.success) {
      setScreenState(SCREEN_STATE.BIOMETRIC_SUCCESS);
      return;
    }

    if (result.reason === 'denied') {
      setScreenState(SCREEN_STATE.BIOMETRIC_DENIED);
    }
  };

  const primaryLabel = biometricEnrollment.isBusy
    ? profile.biometric.settingUp
    : biometricEnrollment.status === BIOMETRIC_ENROLLMENT_STATUS.ERROR
      ? common.tryAgain
      : biometricEnrollment.availability.shouldFinishSetup
        ? profile.biometric.finishSetup
        : t('profile.biometric.enableSpecific', {
          label: biometricEnrollment.biometricLabel,
        });

  return (
    <PinPage>
      <div className="pin-page__body pin-page__body--success">
        <SuccessCheckBadge className="passcode-set__badge" icon={BadgeIcon} />

        <h1 className="passcode-set__title">{title}</h1>
        <p className="passcode-set__subtitle">{subtitle}</p>
      </div>

      {shouldPromoteBiometrics ? (
        <div className="passcode-set__actions">
          <BottomActionButton
            className="passcode-set__primary"
            onClick={handleEnableBiometric}
            disabled={biometricEnrollment.isBusy || isNavigating}
          >
            {primaryLabel}
          </BottomActionButton>
          <button
            type="button"
            className="passcode-set__later"
            onClick={navigateNext}
            disabled={biometricEnrollment.isBusy || isNavigating}
          >
            {common.later}
          </button>
        </div>
      ) : (
        <BottomActionButton
          className="pin-page__cta"
          onClick={navigateNext}
          disabled={isNavigating}
        >
          {common.done}
        </BottomActionButton>
      )}
    </PinPage>
  );
}

export default PasscodeSet;
